import React, { useCallback, useEffect, useState, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import {
  getCertificateControlsById,
  setAllQuestionStatus,
  setAllCertifications,
  setReadOnly,
  setAssessmentStatus,
  QuestionStatusById,
  resetQuestionnaireAnswer,
  setBPStatus,
} from '../../app/store/questionnaireAnswerSlice';
// Hooks
import { useQuestionnaireComplete } from '../../hooks/useQuestionnaireComplete';
import { REMEDIATION_OPTIONS } from '../../services/surveyHelpers';
import { Questionnaire } from '../../interfaces/questionnaire.interface';
import { Domain } from '../../interfaces/domain.interface';
import { Topic } from '../../interfaces/topic.interface';
import { BestPractice } from '../../interfaces/bestPractice.interface';
import { Certification } from '../../interfaces/certification.interface';
import {
  BLUE_BADGE_LABEL,
  CERTIFICATION_BADGE_LABEL,
  awardBlueBadge,
  SILVER_BADGE_LABEL,
  generateGoldShield,
  nextGenEligibilityAwardCheck,
  awardSilverBadge,
  eligibleForSilverBadge,
} from '../../services/badge';
import useFontLoader from '../Directory/UseFontLoader';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';

import QuestionOnQuestionnaireList from '../Questions/QuestionOnQuestionnaireList';
import QuestionnaireSidebar from '../Sidebar/QuestionnaireSidebar';
import BestPracticeHeader from '../BestPractices/BestPracticeHeader';
import { Alert, Button, Stack } from '@mui/material';
import StandardDialog, { StandardDialogActions } from '../Modals/StandardDialog';
import Typography from '@mui/material/Typography';
import { Link, useNavigate } from 'react-router-dom';
import { fetchSurvey, resetSurvey } from '../../app/store/surveySlice';
import { Application, Badge, Company, CompanyCertification, Version } from '../../interfaces/users.interface';
import { Survey } from '../../interfaces/survey.interface';
import { AssessmentSurvey } from '../../interfaces/assessment.interface';
import { updateAssessment } from '../../app/store/assessmentSlice';
import { updateCompanyBoolean } from '../../services/company';
import { updateContact, updateContactShort } from '../../services/contact';
import { updateCompanyApplication, updateCompanyApplicationShort } from '../../services/companyApplication';
import QuestionnaireAnswerLoader from './QuestionnaireAnswerLoader';
import moment from 'moment';
import { debounce } from 'lodash';
import BouncingDotsLoader from '../Forms/Bouncing3dot';
import { createBadge, updateVersionBadge } from '../../services/applications';
import { fetchBaselineSurvey, fetchWorkflow, fetchWorkflowByIdSlice, updateWorkflow } from '../../app/store/workflowSlice';
import { submitAssessment } from '../../services/workflowThunks';
import createSurvey from '../../services/surveys';
import { Workflow, WorkflowTypeKey } from '../../interfaces/workflow.interface';
import { getAssessmentQuestionsAndAnswersCount } from '../../services/surveyHelpers';
import { FileDownload } from '@mui/icons-material';
import { QuestionnaireTypes, fetchQuestionnaireByTitle, isWorkflowPreFiveThreeOne } from '../../services/questionnaires';
import { fetchAll } from '../../app/store/questionnaireSlice';
import { CanNotSetBackToIncompleteWorkflowStatusArray, PRE_ASSESSMENT_STATUSES, WORKFLOW_STATUSES, WorkflowStatus, isAssessmentWfStatus } from '../../services/workflow';
import { workflowEntry } from '../../services/workflow';
import { useWorkflowMap } from '../../hooks/useWorkflowMap';
import { USER_TYPES } from '../../services/user';
import { setNewQuestionInclusionAlert } from '../../app/store/alertSlice';
import { setShowSurveySubmitButton, setShowAssessmentSubmitButton } from '../../app/store/secondaryLayoutButtonSlice';
import { QuestionnaireAnswerHandle } from '../../interfaces/questionAnswer.interface';
import { TOGGLE, ToggleValue } from '../Sidebar/IncompleteToggle';
import { setProcessingAwardGoldBadge } from '../../app/store/workflowProcessingSlice';
import { QUESTIONNAIRE_SUBMISSION_MESSAGE } from '../../utils/uiMessages';
import { surveyMatchesWorkflow } from '../../utils/workflowStorageUtils';
import { ApiErrorAlert } from '../../components/Alerts/ApiErrorAlert';
import { setAnsweredByCertModalData } from '../../app/store/uiSlice';
import { CodeAndBestPracticeType } from '../../hooks/useNeedsCertQARecords';

// Icons
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CircularProgress from '@mui/material/CircularProgress';
import { useIsQuestionnaireArchived } from '../../hooks/useIsQuestionnaireArchived';
import { useGuardedAsync } from '../../hooks/useGuardedAsync';
import QuestionNotFound from '../Helpers/QuestionnaireQuestionNotFound';
import { useAssessmentVariant } from '../Assessments/Workspace/useAssessmentVariant';
import AssessmentWorkspaceView from '../Assessments/Workspace/AssessmentWorkspaceView';

type QuestionnaireAnswerProps = {
  questionnaire: Questionnaire;
  domain?: Domain;
  topic?: Topic;
  bestPractice?: BestPractice;
};

const QuestionnaireAnswer = forwardRef<QuestionnaireAnswerHandle, QuestionnaireAnswerProps>((props, ref) => {
  const {
    questionnaire: questionnaireProp,
    domain: domainProp,
    topic: topicProp,
    bestPractice: bestPracticeProp,
  } = props;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    questionnaire,
    domain,
    topic,
    bestPractice,
    certificationControlIds,
    certControlsById,
    questions,
    allCertifications,
    questionStatus,
    assessmentStatus,
    assessmentQuestionsStatus,
    company,
    bpStatus,
    readOnly,
  } = useAppSelector(state => state.questionnaireAnswer);
  const { processingAwardGoldBadge } = useAppSelector(s => s.workflowProcessingSlice);
  const newQuestionAlerts = useAppSelector(s => s.alertSlice.newQuestionInclusionAlerts);
  const {
    survey,
    mpa_best_practice: mpaBestPractice,
  } = useAppSelector(state => state.surveys);
  const {
    showSubmitSurveyButton,
  } = useAppSelector(state => state.secondaryLayoutButtonSlice);
  const { filteredQuestions, filteredVerificationStatus, filteredPlanStatus, filteredPastDue, filteredQuestionType } = useAppSelector(state => state.remediationFilterSlice);
  const { assessment } = useAppSelector(state => state.assessments);
  const { user } = useAppSelector(state => state.user);
  const { baseline, workflow } = useAppSelector(state => state.workflow);
  const [questionnaireComplete, setQuestionnaireCompleteOpen] = useState(false);
  const [showQuestionnaireNotFound, setShowQuestionnaireNotFound] = useState(false);
  // Assessor-experience-v2: same route, loaders, effects, dialogs — only the rendered layout swaps under an assessment.
  const isAssessmentVariant = useAssessmentVariant();

  const { showArchivedAlert, dismissArchivedAlert } =
    useIsQuestionnaireArchived({
      archiveDate: questionnaire?.archiveDate,
      badges: workflow?.badges,
      workflowStatus: workflow?.status,
    });

  const handleQuestionnaireCompleteOpen = () => {
    dispatch(setShowSurveySubmitButton(false));
    setQuestionnaireCompleteOpen(true);
  };
  const [shouldOpenQuestionnaireModal, setShouldOpenQuestionnaireModal] = useState(user?.type === USER_TYPES.VENDOR_ADMIN);
  const [questionnaireSubmitted, setQuestionnaireSubmittedOpen] = useState(false);
  const handleQuestionnaireSubmittedOpen = () => setQuestionnaireSubmittedOpen(true);
  const handleQuestionnaireSubmittedClose = () => setQuestionnaireSubmittedOpen(false);
  const [showBaselineCompleteModal, setShowBaselineCompleteModal] = useState(false);
  const [questionnaireSubmittedConfirm, setQuestionnaireSubmittedConfirmOpen] = useState(false);
  const [isSubmittingForAssessment, setIsSubmittingForAssessment] = useState(false);
  const [showSubmitForAssessmentError, setShowSubmitForAssessmentError] = useState(false);

  const isUserCompanyAdmin = useMemo(() => {
    if (!user?.id || user?.type !== USER_TYPES.VENDOR_ADMIN) return false;

    const companies = user.companies ?? [];
    return companies.some(c =>
      (c.admins ?? []).some(a => (typeof a === 'number' ? a : a.id) === user.id),
    );
  }, [user?.id, user?.type, user?.companies]);


  const handleQuestionnaireSubmittedConfirmClose = useCallback((isQuestionnaire?: boolean) => {
    if (isQuestionnaire && isUserCompanyAdmin) {
      dispatch(setShowSurveySubmitButton(true));
    }
    setShowSubmitForAssessmentError(false);
    setQuestionnaireSubmittedConfirmOpen(false);
  }, [dispatch, isUserCompanyAdmin]);

  const [assessmentComplete, setAssessmentCompleteOpen] = useState(false);
  const handleAssessmentCompleteOpen = () => setAssessmentCompleteOpen(true);
  const handleAssessmentCompleteClose = () => setAssessmentCompleteOpen(false);
  const [assessmentSubmitted, setAssessmentSubmittedOpen] = useState(false);
  const handleAssessmentSubmittedOpen = () => setAssessmentSubmittedOpen(true);
  const handleAssessmentSubmittedClose = () => setAssessmentSubmittedOpen(false);
  const [remediationCompleted, setRemediationCompletedOpen] = useState(false);
  const handleRemediationCompletedOpen = () => setRemediationCompletedOpen(true);
  const handleRemediationCompletedClose = () => setRemediationCompletedOpen(false);
  const [siteOrApplicationName, setSiteOrApplicationName] = useState<string | undefined>(undefined);
  const [shouldOpenModal, setShouldOpenModal] = useState(true);
  const [newSurveyId, setnewSurveyId] = useState<number | undefined>(undefined);
  const questionnaires: Questionnaire[] = useAppSelector(state => state.questionnaires.results);
  const workflowMap = useWorkflowMap();
  const [showNavigateToQuestionnaireButton, setShowNavigateToQuestionnaireButton] = useState(false);
  const [dismissIncompleteSurvey, setDismissIncompleteSurvey] = useState(false);
  const [showIncompleteOnly, setShowIncompleteOnly] = useState(false);
  const [isSubmittingAssessment, setIsSubmittingAssessment] = useState(false);
  const fetchingSurveyRef = useRef<number | null>(null);
  const runGuardedAsync = useGuardedAsync(fetchingSurveyRef);

  const handleToggleIncomplete = useCallback((value: ToggleValue) => {
    setShowIncompleteOnly(value !== TOGGLE.ALL);
  }, []);

  const { baselineCompleted, questionnaireCompleted, incompleteQuestionIds } = useQuestionnaireComplete({
    baseline,
    questionStatus,
    questionnaire,
    isQuestionniareBestPractice: !!survey && (survey.questionnaire as Questionnaire)?.type === QuestionnaireTypes.MPA_BEST_PRACTICE,
    workflowType: workflow?.workflowType as WorkflowTypeKey | undefined,
  });
  const isQuestionnaireComplete = useMemo(() => baselineCompleted && questionnaireCompleted, [baselineCompleted, questionnaireCompleted]);
  const prevCompleteRef = useRef(isQuestionnaireComplete);
  const [loadingAssessmentQACount, setLoadingAssessmentQACount] = useState(true);

  const handleSetShowAutoAnsweredByCertModal = useCallback((cert: CompanyCertification) => {
    const title =
      typeof cert.certification === 'object'
        ? cert.certification.title
        : String(cert.certification);

    const arCodes: Record<string, CodeAndBestPracticeType> = {};
    const bpCodes: Record<string, CodeAndBestPracticeType> = {};

    Object.values(questionStatus).forEach(qs => {
      qs.question.excludedByCertifications?.forEach(exclusion => {
        const exclusionId = typeof exclusion === 'number' ? exclusion : exclusion.id;
        const ctrl = certControlsById?.[String(exclusionId)];
        const certId =
          typeof cert.certification === 'object'
            ? cert.certification.id
            : cert.certification;

        if (
          qs.status === 'cert' &&
          ctrl?.certification?.id === certId &&
          qs.question.shortCode
        ) {
          const completeBp = questionnaire?.domains
            .find(d => d.id === (typeof qs.question.domain === 'number' ? qs.question.domain : qs.question.domain.id))?.topics
            .find(t => t.id === (typeof qs.question.topic === 'number' ? qs.question.topic : qs.question.topic.id))?.bestPractices
            .find(bp => bp.id === (typeof qs.question.bestPractice === 'number' ? qs.question.bestPractice : qs.question.bestPractice.id))!;

          if (qs.question.isBestPractice) {
            bpCodes[qs.question.shortCode] = {
              code: qs.question.shortCode,
              bestPractice: completeBp,
            };
          } else {
            arCodes[qs.question.shortCode] = {
              code: qs.question.shortCode,
              bestPractice: completeBp,
            };
          }
        }
      });
    });

    dispatch(setAnsweredByCertModalData({
      titles: [title],
      arCodes: Object.values(arCodes),
      bpCodes: Object.values(bpCodes),
    }));
  }, [dispatch, questionStatus, certControlsById]);

  const assessmentQuestionAnswerCount = useMemo(() => {
    return getAssessmentQuestionsAndAnswersCount(bpStatus, assessmentQuestionsStatus, questionnaire, workflow?.status as WorkflowStatus);
  }, [assessment, survey, assessmentStatus, bpStatus, assessmentQuestionsStatus]);

  const handleFetchBaseline = useCallback((id: number) => {
    return runGuardedAsync(
      id,
      () => dispatch(fetchBaselineSurvey({ id })),
    );
  }, [dispatch, runGuardedAsync]);

  useEffect(( ) => {
    if (!assessmentQuestionAnswerCount.isComplete) {
      handleAssessmentCompleteClose();
      // Re-arm the auto-open: transient incompleteness while loaders rewrite assessmentQuestionsStatus must not consume the one-shot.
      setShouldOpenModal(true);
    }
  }, [assessmentQuestionAnswerCount.isComplete]);

  useEffect(() => {
    dispatch(fetchAll({ unpublished: false }));
    dispatch(setShowSurveySubmitButton(false));
    dispatch(setShowAssessmentSubmitButton(false));
  }, []);

  useEffect(() => {
    if (!company || !survey || (!survey.application && !survey.site)) return;

    const site = survey.site;
    const application = survey.application;

    if (site) setSiteOrApplicationName(company.locations?.find(loc => loc.id === site)?.name);
    if (application) setSiteOrApplicationName(company.companyApplications?.find(app => app.application.id === application)?.application.name);
  }, [company, survey]);

  // load OR update workflow object if there's a survey
  /* KYLE: in this function we need to determine how to get the workflow. There are 3 possibilities:
    1. This is a baseline survey. URL will be /questionnaire/{id}/ and survey.quetsionnaire.type will be either application_baseline or site_baseline. Get workflow via /workflows/?baseline={survey.id}
    2. This is a best practice survey. URL will be /qustionnaire/{id}/ and survey.questionnaire.type will be mpa_best_practice. Get workflow via /workflows/?bpSurvey={survey.id}
    3. This is an assessment. URL will be /assessment/{id}/questionnaire/{id}/. Get workflow via /workflows/?bpSurvey={survey.id}&assessment={assessment.id}
  */
  useEffect(() => {
    if (survey && (!workflow || (workflow.bpSurvey !== survey.id && workflow.baseline !== survey.id && (!assessment || workflow.assessment !== assessment.id)))) dispatch(fetchWorkflow({ survey, assessment }));
  }, [survey, assessment]);

  // fetch baseline survey if there's a survey, questionnaire and a workflow
  // KYLE: We only need to fetch the baseline if this is a best practice questionnaire or assessment. Probably best to wait until we have the workflow here then just request the baseline via the baseline id in the workflow if needed.
  useEffect(() => {
    if (survey && questionnaire && workflow && (!baseline || workflow.baseline !== baseline.id)) {
      handleFetchBaseline(workflow.baseline!);
    }
    //KYLE: at this point we should have everything we had before and the flow can continue as normal
  }, [survey, questionnaire, workflow]);

  useEffect(() => {
    if (!certControlsById) return;

    let certs:Certification[] = [];
    Object.keys(certControlsById).forEach(certControlId => {
      const index = certs.findIndex(cert => cert.id === certControlsById[certControlId].certification.id);
      if (index === -1) certs.push(certControlsById[certControlId].certification);
    });
    dispatch(setAllCertifications(certs));
  }, [certControlsById]);

  useEffect(() => {
    if (!allCertifications || !questions || !questionnaire) return;
    const questionStatusById:QuestionStatusById = {};
    if (survey && (survey.questionnaire as Questionnaire).type === QuestionnaireTypes.MPA_BEST_PRACTICE) {
      // instead of filter, fetch baseline based on workflow
      // KYLE: lets make sure if we between survey, assessment, and questionnaireAnswer we are only fetching the baseline once and everywhere else references that object
      // const baseline = await fetchBaselineSurvey(questionnaire.id, (survey.site || survey.application) as number, workflow.applicationVersion);
      // T: stored the baseline (Survey[]) inside the workflow slice, instead of fetching it here since it's going to be referenced somewhere else in the app
      const addQAs = (array:Survey) => {
        array.questionAnswers.forEach(qa => {
          if (typeof qa === 'number') return;
          questionStatusById[qa.question] = {
            status: 'offSurvey',
            questionAnswer: qa,
            question: {
              title: 'none',
              slug: 'none',
              version: 'none',
              excludedByTrainings: [],
              questionnaire: 0,
              domain: 0,
              topic: 0,
              bestPractice:0,
              includeAttachmentForm: false,
              includeComment: false,
              excludedByCertifications: [],
              inclusions: [],
              answers: [],
              isBestPractice: false,
            },
          };
        });
      };

      if (baseline) addQAs(baseline);
    }
    dispatch(setAllQuestionStatus(questionStatusById));
  }, [allCertifications, questions, baseline]);

  const showQuestionnaireCompleteModal = useMemo(() => {
    return !!questionnaireComplete
      && !!questionnaire
      && !questionnaireSubmittedConfirm
      && !showSubmitSurveyButton
      && !workflow?.badges?.length
      && workflow?.isActive;
  }, [
    questionnaireComplete,
    questionnaire,
    questionnaireSubmittedConfirm,
    showSubmitSurveyButton,
    workflow?.badges,
    workflow?.isActive,
  ]);

  const isDev = () => {
    //if (process.env.REACT_APP_ENVIRONMENT === 'dev' || window.location.host.indexOf('qa-tpn') > -1) return true;
    return false;
  };

  useEffect(() => {
    if (!survey) return;

    if ( user?.type !== USER_TYPES.VENDOR_ADMIN ) {
      dispatch(setReadOnly(true));
      return;
    }

    const hasCompletedBoth = Boolean(questionnaireCompleted && baselineCompleted);
    if (survey.status === 'submittedForAssessment' && (!assessmentStatus || assessmentStatus !== 'Pre-Assessment') && questionnaire && !readOnly) {
      if (!workflow?.badges?.some((badge) => typeof badge !== 'number' && badge.title === BLUE_BADGE_LABEL)) {
        handleQuestionnaireSubmittedOpen();
      }
      dispatch(setReadOnly(true));
    }

    if ((['submittedForAssessment', 'priority'].indexOf(survey.status) > -1 || survey.status.indexOf('remediation') > -1) &&
      (!assessmentStatus || assessmentStatus !== 'Pre-Assessment')
    ) dispatch(setReadOnly(true));

    if (!questionnaire || questionnaire.type !== QuestionnaireTypes.MPA_BEST_PRACTICE) return;
    if (isDev() && !!workflow?.isActive && questionnaire && questionnaire.type === QuestionnaireTypes.MPA_BEST_PRACTICE && !assessmentStatus && survey.status !== 'submittedForAssessment' && !showQuestionnaireCompleteModal && isUserCompanyAdmin) {
      dispatch(setShowSurveySubmitButton(true));
      return;
    }
    if (!isDev() && survey.status === 'complete' && questionnaire && questionnaire.type === QuestionnaireTypes.MPA_BEST_PRACTICE && isUserCompanyAdmin) {
      if (shouldOpenQuestionnaireModal) {
        setShouldOpenQuestionnaireModal(false);
        handleQuestionnaireCompleteOpen();
      }
      if (!showQuestionnaireCompleteModal || !hasCompletedBoth) return;
      dispatch(setShowSurveySubmitButton(true));
      return;
    }

    const hasBadges = Boolean(workflow?.badges?.length);
    if (!hasCompletedBoth || hasBadges) {
      dispatch(setShowSurveySubmitButton(false));
      return;
    }

  }, [survey, questionnaire, mpaBestPractice]);

  useEffect(() => {
    if (assessmentStatus && assessmentStatus === 'Pre-Assessment' && readOnly) dispatch(setReadOnly(false));
    if (assessmentStatus && assessmentStatus !== 'Pre-Assessment' && !readOnly) dispatch(setReadOnly(true));
  }, [assessmentStatus]);

  useEffect(() => {
    if (!survey || !assessment || user?.type !== USER_TYPES.ASSESSOR || !questionnaire || !assessmentQuestionsStatus) return;

    const curSurvey = (assessment.surveys as AssessmentSurvey[]).find(assSur => typeof assSur.survey === 'number' ? assSur.survey === survey.id : assSur.survey.id === survey.id);
    if (!curSurvey) {
      dispatch(setShowAssessmentSubmitButton(false));
      return;
    }

    const { hasQuestion, hasBP, actualAssessmentAnswers, isComplete } = assessmentQuestionAnswerCount || {};
    if (!hasQuestion || !hasBP) return;
    if (isDev() && actualAssessmentAnswers?.length && assessmentStatus === 'Assessing' && user?.type === USER_TYPES.ASSESSOR) {
      dispatch(setShowAssessmentSubmitButton(true));
      return;
    }
    if (!isDev() && isComplete && assessmentStatus === 'Assessing') {
      if (shouldOpenModal) {
        // Delay to allow UI to update
        setTimeout(() => {
          setLoadingAssessmentQACount(false);
        }, 2500);
        setShouldOpenModal(false);
        handleAssessmentCompleteOpen();
      }
      if (user?.type === USER_TYPES.ASSESSOR) dispatch(setShowAssessmentSubmitButton(true));
      return;
    }

    dispatch(setShowAssessmentSubmitButton(false));
  }, [assessment, survey, assessmentStatus, bpStatus, assessmentQuestionsStatus, assessmentQuestionAnswerCount]);

  const isCurrWfPreFiveThree = useMemo(() => workflow?.workflowType && isWorkflowPreFiveThreeOne(workflow.workflowType), [workflow?.workflowType]);

  useEffect(() => {
    if (questionnaire && certificationControlIds) dispatch(getCertificateControlsById(certificationControlIds));
  }, [certificationControlIds]);


  const submitForAssessment = useCallback(async () => {
    dispatch(setReadOnly(true));
    if (!survey || !workflow) return;

    setIsSubmittingForAssessment(true);
    setShowSubmitForAssessmentError(false);

    try {
      await awardBlueBadge(workflow.id);
      await dispatch(fetchWorkflowByIdSlice(workflow.id));
      await dispatch(fetchSurvey(survey.id));
      handleQuestionnaireSubmittedConfirmClose();
      handleQuestionnaireSubmittedOpen();
    } catch (error) {
      console.error('Error awarding blue badge:', error);
      setShowSubmitForAssessmentError(true);
    } finally {
      setIsSubmittingForAssessment(false);
    }

  }, [survey, workflow, dispatch, handleQuestionnaireSubmittedOpen, handleQuestionnaireSubmittedConfirmClose]);

  const submitForApproval = async () => {
    handleAssessmentCompleteClose();
    handleAssessmentSubmittedOpen();

    if (!workflow) return;
    setIsSubmittingAssessment(true);
    try {
      await dispatch(submitAssessment(workflow.id));
    } catch (error) {
      console.error('Error submitting for approval:', error);
    } finally {
      await dispatch(fetchWorkflowByIdSlice(workflow.id));
      setIsSubmittingAssessment(false);
    }
  };

  const loadFont = useFontLoader('Montserrat', 700);
  const handleGenerateGoldShield = useCallback( async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (!workflow || !company) return;
    const isFontLoaded = await loadFont();
    if (!isFontLoaded) {
      return;
    }

    generateGoldShield({
      company,
      site: company.locations?.find(loc => loc.id ===  workflow?.site),
      workflow,
    });
  }, [workflow, company]);

  const postSilverBadge = useCallback(async (workflowId: number) => {
    try {
      const result = await awardSilverBadge(workflowId);
      if (!result) throw new Error('Silver badge award returned null');
      handleRemediationCompletedOpen();
    } finally {
      dispatch(fetchWorkflowByIdSlice(workflowId));
    }
  }, [handleRemediationCompletedOpen]);

  const validateSilverBadgeEligibility = useCallback(
    async (workflowId: number): Promise<boolean> => {
      try {
        const eligibility = await eligibleForSilverBadge(workflowId);

        if (!eligibility || 'error' in eligibility) {
          console.error('Silver badge eligibility check failed:', eligibility?.error ?? 'No response');
          return false;
        }

        await postSilverBadge(workflowId);
        await nextGenEligibilityAwardCheck(workflowId);
        return true;
      } catch (error) {
        console.error('Unexpected error in validateSilverBadgeEligibility:', error);
        return false;
      }
    },
    [postSilverBadge],
  );

  useEffect(() => {
    dispatch(setBPStatus(workflow?.workflowType));

    if (!assessmentQuestionsStatus || !assessmentStatus || assessmentStatus !== 'Remediation' || !assessment || !survey) return;
    const assQKeys = Object.keys(assessmentQuestionsStatus);
    if (assQKeys.length === 0) return;

    let complete = true;
    for (let i = 0; i < assQKeys.length; i++) {
      const currAssessment = assessmentQuestionsStatus[assQKeys[i]];
	    const nonBlockingRemediation = ['submitted', 'remediation', 'priority'].includes(currAssessment?.status || '') && [REMEDIATION_OPTIONS.willNotRemediate, REMEDIATION_OPTIONS.willRemediateLater].includes(currAssessment.vendorStatus || '');
      if ((currAssessment.status?.toLowerCase() !== 'remediation' && currAssessment.status?.toLowerCase() !== 'priority') || nonBlockingRemediation) continue;
      complete = false;
      break;
    }
    if (!complete) return;

    const hasSilverBadge = workflow?.badges?.some(b => typeof b !== 'number' && b.title === SILVER_BADGE_LABEL);
    const curSurvey = (assessment.surveys as AssessmentSurvey[]).find(assSur => typeof assSur.survey === 'number' ? assSur.survey === survey.id : assSur.survey.id === survey.id);
    if (!curSurvey || processingAwardGoldBadge || !workflow?.id || hasSilverBadge) return;

    const date = curSurvey.reportIssueDate ? moment(curSurvey.reportIssueDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');

    dispatch(setProcessingAwardGoldBadge(true));

    validateSilverBadgeEligibility(workflow.id).then((success) => {
      if (!success) return;

      if (company) updateCompanyBoolean({ id: company.id, displayShieldType: SILVER_BADGE_LABEL, goldShieldDate: date });
      if ((curSurvey.survey as Survey).site && workflow?.status !== WORKFLOW_STATUSES.FINAL_COMPLETE) {
        updateContactShort({ id: (curSurvey.survey as Survey).site, badge: SILVER_BADGE_LABEL, goldShieldDate: date }); 
      }
      if ((curSurvey.survey as Survey).application && workflow?.status !== WORKFLOW_STATUSES.FINAL_COMPLETE) {
        const companyApplication = company?.companyApplications?.find(ca => ca.application.id === (curSurvey.survey as Survey).application);
        if (!companyApplication) return;

        if (companyApplication.badge !== SILVER_BADGE_LABEL) updateCompanyApplicationShort({ id: companyApplication.id, badge: SILVER_BADGE_LABEL, goldShieldDate: date });
        handleRemediationCompletedOpen();
      }
      dispatch(setAssessmentStatus(WORKFLOW_STATUSES.FINAL_COMPLETE));
      dispatch(updateAssessment({ ...assessment, status: WORKFLOW_STATUSES.FINAL_COMPLETE }));
    }).finally(() => {
      dispatch(setProcessingAwardGoldBadge(false));
    });
  }, [questionStatus, assessmentQuestionsStatus, assessmentStatus, assessment, survey]);

  const callBackgroundCreateSurvey = useCallback(async (dsurvey: Survey, dmpaBestPractice: number, dworkflow: Workflow) => {
    if (!dsurvey || !dmpaBestPractice || !dworkflow?.workflowType || dworkflow.bpSurvey) return;

    if (!surveyMatchesWorkflow(dsurvey, dworkflow)) {
      console.error('Blocked BP survey creation: survey scope does not match workflow.', { surveyId: dsurvey.id, workflowId: dworkflow.id });
      return;
    }

    const wfEntry = workflowEntry(dworkflow.workflowType);
    let wfQuestionnaire: Questionnaire | undefined;

    // Resolve the workflow's questionnaire: match by workflow type first (titles are not
    // unique across duplicated versions), falling back to a title fetch.
    if (!(questionnaires ?? []).length) {
      const { results } = await fetchQuestionnaireByTitle(wfEntry?.questionnaireTitle ?? '');
      wfQuestionnaire = results.find(q => q.workflowType === dworkflow.workflowType) ?? results[0];
    } else {
      wfQuestionnaire = questionnaires.find(q => q.workflowType === dworkflow.workflowType)
        ?? questionnaires.find(q => q.title === wfEntry?.questionnaireTitle);
    }

    const bpSurvey = await createSurvey({
      questionnaire: wfQuestionnaire?.id ?? dmpaBestPractice,
      status: 'incomplete',
      company: (dsurvey.company as Company).id!,
      questionAnswers: [],
      site: dsurvey.site,
      application: dsurvey.application,
      version: dsurvey.version,
    });

    if (!bpSurvey?.id) {
      console.error('Failed to create survey.');
      return;
    }

    await dispatch(updateWorkflow({ id: dworkflow.id, status: 'incomplete', bpSurvey: bpSurvey.id! }));
    setnewSurveyId(bpSurvey.id);
  }, [questionnaires]);

  const debounceBackgroundCreateSurvey = useCallback(debounce((dsurvey, dmpaBestPractice, dworkflow) => {
    callBackgroundCreateSurvey(dsurvey, dmpaBestPractice, dworkflow);
  }, 1000), []);

  useEffect(() => {
    if (!Object.keys(questionStatus).length || !bpStatus || !questionnaire) return;
    let surveyComplete = true;
    let hasBP = false;
    let hasQuestion = false;

    questionnaire.domains.forEach(d => {
      d.topics.forEach(t => {
        t.bestPractices.forEach(bp => {
          if (!bpStatus[bp.id] || bpStatus[bp.id].status === 'hide') return;
          if (bpStatus[bp.id].status !== 'hide') hasBP = true;
          bp.questions.forEach(q => {
            if (!questionStatus[q.id] || questionStatus[q.id].status === 'incomplete' || (questionStatus[q.id].questionAnswer && !questionStatus[q.id].questionAnswer?.id )) surveyComplete = false;
            hasQuestion = true;
          });
        });
      });
    });
    if (!surveyComplete || !hasBP || !hasQuestion) return;
    const shouldOpenCompleteModal = !questionnaireSubmittedConfirm && shouldOpenQuestionnaireModal && !assessment && survey?.status !== 'submittedForAssessment';
    if (survey && ((survey.questionnaire as Questionnaire).type === QuestionnaireTypes.SITE_BASELINE || (survey.questionnaire as Questionnaire).type === QuestionnaireTypes.APPLICATION_BASELINE)) {
      // Baseline
      if (!mpaBestPractice || !workflow) return;
      if (!workflow.bpSurvey) debounceBackgroundCreateSurvey(survey, mpaBestPractice, workflow);
      if (survey?.status === 'complete' && (
        (survey.site && survey.site === workflow?.site) ||
        (survey.application && survey.application === workflow?.application || survey.application === (workflow?.application as Application)?.id)
      )) return;

      if (workflow.status && workflow.status !== 'incomplete' && !CanNotSetBackToIncompleteWorkflowStatusArray.includes(workflow.status as WorkflowStatus)) {
        dispatch(updateWorkflow({ id: workflow.id, status: 'incomplete' }));
      }

      if (workflow.bpSurvey) {
        // Baseline answers changed revealing more questions that are now complete
        setShowNavigateToQuestionnaireButton(true);
      }
      if (shouldOpenCompleteModal) {
        setShouldOpenQuestionnaireModal(false);
        handleQuestionnaireCompleteOpen();
      }
      return;
    }

    // Questionnaire
    if (shouldOpenCompleteModal) {
      setShouldOpenQuestionnaireModal(false);
      handleQuestionnaireCompleteOpen();
    }
  }, [questionStatus, survey, mpaBestPractice, workflow]);

  useEffect(() => {
    if (!questionnaire || questionnaire.type !== QuestionnaireTypes.MPA_BEST_PRACTICE || !survey || !company) return;
    const cert = company.companyCertifications?.find(tcert => {
      if (tcert.certification.company) return;
      if (survey.application) return tcert.applications.find(app => app.id === survey.application);
      if (survey.site) return tcert.sites.find(site => site.id === survey.site);
    });
    if (!cert) return;
    if (survey.site) {
      const site = company.locations.find(location => location.id === survey.site);
      if (site && !site.badge) updateContact({ ...site, primaryContact: site.primaryContact ? site.primaryContact.id : undefined, badge: CERTIFICATION_BADGE_LABEL });
    }
    if (survey.application) {
      const application = company.companyApplications?.find(capplication => capplication.application.id === survey.application);
      if (application) {
        const version = (application.application.versions as Version[]).find(v => v.value === survey.version);
        if (version && !version.badge) {
          const date = moment().format('YYYY-MM-DD');
          createBadge({
            title: CERTIFICATION_BADGE_LABEL,
            awardedDate: date,
            expirationDate: moment(date).add(2, 'years').format('YYYY-MM-DD'),
          }).then(newBadge => {
            updateVersionBadge(version.id!, newBadge.id!);
          });
        }

        if (!application.badge) {
          updateCompanyApplication({
            company: application.company,
            id: application.id,
            application: application.application.id!,
            sites: application.sites ? application.sites.map(site => site.id!) : undefined,
            services: application.services.map(service => service.id),
            badge: CERTIFICATION_BADGE_LABEL,
          });
        }
      }
    }
    if (!company.displayShieldType ||
      company.displayShieldType === '' ||
      company.displayShieldType === 'tpn_member'
    ) {
      updateCompanyBoolean({
        id: company.id,
        displayShieldType: CERTIFICATION_BADGE_LABEL,
      });
    }
  }, [company?.companyCertifications]);

  const relavantQuestionStatus = useMemo(() => {
    if (!bestPractice || bestPractice.questions.length === 0) {
      return {};
    }

    return bestPractice.questions.reduce((acc, question) => {
      acc[question.id] = questionStatus[question.id];
      return acc;
    }, {} as QuestionStatusById);
  }, [bestPractice?.questions, questionStatus]);

  const [hideLoader, setHideLoader] = useState(false);

  const navigateAndReload = useCallback(() => {
    dispatch(resetSurvey());
    dispatch(resetQuestionnaireAnswer());
    dispatch(fetchBaselineSurvey({ id: workflow?.baseline! }));
    setTimeout(() => {
      navigate(`/questionnaire/${showNavigateToQuestionnaireButton ? workflow?.bpSurvey : newSurveyId}/`);
    }, 500);
    setHideLoader(false);
    setShowNavigateToQuestionnaireButton(false);
  }, [newSurveyId, showNavigateToQuestionnaireButton, workflow]);

  const filteredQuestionsList = useMemo(() => {
    if (!bestPractice?.questions) {
      return [];
    }

    // Return all questions if no filters exist
    if (!filteredQuestions || Object.values(filteredQuestions).length === 0) {
      return bestPractice.questions;
    }

    return bestPractice.questions.filter(question => filteredQuestions[question.id]);
  }, [filteredQuestions, bestPractice?.questions]);

  const isQuestionniareBaseline = useMemo(() => {
    return questionnaire?.type === QuestionnaireTypes.SITE_BASELINE || questionnaire?.type === QuestionnaireTypes.APPLICATION_BASELINE;
  }, [questionnaire]);

  const showBaselineIncomplete = useMemo(() => {
    if (!workflow || baselineCompleted || !baseline) return false;

    const validWfStatuses = workflow.bpSurvey ?
      [...PRE_ASSESSMENT_STATUSES, WORKFLOW_STATUSES.COMPLETE, WORKFLOW_STATUSES.NON_COMPLIANT] :
      [...PRE_ASSESSMENT_STATUSES, WORKFLOW_STATUSES.COMPLETE];

    return validWfStatuses.includes(workflow.status as WorkflowStatus);
  }, [workflow?.status, baselineCompleted, workflow?.bpSurvey, baseline]);

  const showQuestionaireIncomplete = useMemo(() => {
    return workflow && PRE_ASSESSMENT_STATUSES.includes(workflow.status as WorkflowStatus) && !questionnaireCompleted && !isQuestionniareBaseline;
  }, [workflow?.status, questionnaireCompleted, isQuestionniareBaseline]);

  const visibleNewQuestionAlerts = useMemo(() => Object.keys(newQuestionAlerts).filter(qId => !!newQuestionAlerts[qId]), [newQuestionAlerts]);

  useEffect(() => {
    setDismissIncompleteSurvey(false);
  }, [baseline?.questionAnswers]);

  useEffect(() => {
    const hasOnlyCertificationBadge =
      workflow?.badges?.length === 1 &&
      (workflow.badges[0] as Badge).title === CERTIFICATION_BADGE_LABEL;
    if (
      (isQuestionnaireComplete || baselineCompleted) &&
      !prevCompleteRef.current &&
      user?.type === USER_TYPES.VENDOR_ADMIN &&
      (
        !workflow?.badges?.length || hasOnlyCertificationBadge
      )
    ) {
      if (isQuestionniareBaseline && workflow?.status !== WORKFLOW_STATUSES.COMPLETE && !workflow?.bpSurvey) {
        setShowBaselineCompleteModal(true);
      } else if (!isQuestionniareBaseline && !workflow?.assignedAssessor) {
        setQuestionnaireSubmittedConfirmOpen(true);
      }
    }

    // update ref for next render
    prevCompleteRef.current = isQuestionnaireComplete;
  }, [isQuestionnaireComplete, workflow?.badges, isQuestionniareBaseline, workflow?.status, baselineCompleted]);

  const showSubmitQuestionnaireDialog = useCallback(() => {
    setQuestionnaireSubmittedConfirmOpen(true);
  }, [setQuestionnaireSubmittedConfirmOpen]);

  useImperativeHandle(ref, () => ({
    showSubmitQuestionnaireDialog,
    showSumbitAssessmentDialog: handleAssessmentCompleteOpen,
  }), [showSubmitQuestionnaireDialog, submitForApproval]);

  const incompleteAssessmentQuestionIds = useMemo<Record<number, number>>(() => {
    if (
      !assessmentQuestionAnswerCount?.incompleteQuestionIds ||
      !workflow?.status ||
      !isAssessmentWfStatus(workflow.status)
    ) return {};

    const idObject: Record<number, number> = {};
    for (const qId in questionStatus) {
      const qStatus = questionStatus[qId];
      if (Boolean(qStatus.status !== 'hide' && assessmentQuestionAnswerCount.incompleteQuestionIds[Number(qId)])) {
        idObject[Number(qId)] = assessmentQuestionAnswerCount.incompleteQuestionIds[Number(qId)];
      }
    }
    return idObject;
  }, [assessmentQuestionAnswerCount, workflow?.status, questionStatus]);

  const noQuestionsVisible = useMemo(() => {
    const incompleteIds = showIncompleteOnly && workflow?.status ? isAssessmentWfStatus(workflow.status) ? incompleteAssessmentQuestionIds : incompleteQuestionIds : undefined;
    if (!incompleteIds) return false;

    return filteredQuestionsList.every(q => !Boolean(incompleteIds[q.id]));
  }, [showIncompleteOnly, workflow?.status, incompleteAssessmentQuestionIds, incompleteQuestionIds, filteredQuestionsList]);

  const hasActiveRemediationFilter = Boolean(
    filteredVerificationStatus || filteredPlanStatus || filteredPastDue || filteredQuestionType,
  );

  const handleShowQuestionnaireNotFound = useCallback(() => {
    setHideLoader(true);
    setShowQuestionnaireNotFound(true);
  }, [setHideLoader]);

  const showQuestionnaireAnswerLoader = (!hideLoader || !questionnaireProp || !questionnaire || !baseline);

  if (!showQuestionnaireNotFound && showQuestionnaireAnswerLoader) {
    return (
      <QuestionnaireAnswerLoader
        questionnaire={questionnaireProp}
        domain={domainProp}
        topic={topicProp}
        bestPractice={bestPracticeProp}
        setHideLoader={setHideLoader}
        setShowQuestionnaireNotFound={handleShowQuestionnaireNotFound}
      />
    );
  }

  if ((!filteredQuestionsList.length && !hasActiveRemediationFilter) || showQuestionnaireNotFound) {
    return (
      <QuestionNotFound
        assessmentId={assessment?.id}
        surveyId={survey?.id}
        />
    );
  }

  if (!questionnaire || !baseline) {
    return (
      <QuestionNotFound
        assessmentId={assessment?.id}
        surveyId={survey?.id}
      />
    );
  }


  const wfQuestionnaireVersion = workflowMap[workflow?.workflowType || '']?.version ?? '';

  return (
        <Grid container sx={{ maxWidth: '100%' }} spacing={isAssessmentVariant ? 0 : 1} mt={isAssessmentVariant ? 0 : 3} mb={isAssessmentVariant ? 0 : 3}>
          {isAssessmentVariant ? (
          <Grid item xs={12}>
            <AssessmentWorkspaceView onSubmitAssessment={handleAssessmentCompleteOpen} />
          </Grid>
          ) : (
          <>
          <Grid item xs={12} md={questionnaire!.type === QuestionnaireTypes.MPA_BEST_PRACTICE ? 8 : 12}>
          { (showArchivedAlert || showQuestionaireIncomplete || showBaselineIncomplete || (questionnaire?.type !== QuestionnaireTypes.MPA_BEST_PRACTICE && visibleNewQuestionAlerts.length > 0)) && (
            <Stack
              component="header"
              spacing={1}
              sx={{
                position: 'sticky',
                top: 0,
                left: 0,
                right: 0,
                zIndex: theme => theme.zIndex.appBar,
                width: '100%',
                mb: 2,
              }}
            >
            {visibleNewQuestionAlerts.map(questionId =>
              newQuestionAlerts[questionId] ? (
                <Alert
                  key={questionId}
                  severity="warning"
                  onClose={() =>
                    dispatch(
                      setNewQuestionInclusionAlert({ questionId, visible: false }),
                    )
                  }
                  sx={{ mb: 1, borderRadius: 0 }}
                >
                  <strong>{questionStatus[questionId].question.title}</strong> updated. Complete the newly unlocked questions in your Best Practice Questionnaire.
                </Alert>
              ) : null,
            )}
            {showBaselineIncomplete && !dismissIncompleteSurvey && (
              <Alert
                severity="warning"
                onClose={() => setDismissIncompleteSurvey(true)}
              >
                <strong>Baseline</strong> incomplete. Answer all questions so your Assessor can proceed with Findings.
              </Alert>
            )}
            {showQuestionaireIncomplete && !dismissIncompleteSurvey && (
              <Alert
                severity="warning"
                onClose={() => setDismissIncompleteSurvey(true)}
              >
                <strong>Questionnaire</strong> incomplete. Answer all questions so your Assessor can proceed with Findings.
              </Alert>
            )}
            { /* Archived Questionnaire version alert */ }
            { showArchivedAlert && (
              <Alert
                severity="warning"
                onClose={dismissArchivedAlert}
                >
                This TPN Questionnaire version is archived. It is locked and read-only.
              </Alert>
            )}
          </Stack>
          )}
             { domain && topic && bestPractice && questionnaire && <BestPracticeHeader bestPractice={{ ...bestPractice, domain, topic, questionnaire }} /> }
             { bestPractice && (
             <QuestionOnQuestionnaireList
              incompleteQuestionIds={showIncompleteOnly && workflow?.status ? isAssessmentWfStatus(workflow.status) ? incompleteAssessmentQuestionIds : incompleteQuestionIds : undefined}
              questions={filteredQuestionsList}
              relavantQuestionStatus={relavantQuestionStatus}
              />
             )}

             {(noQuestionsVisible || (hasActiveRemediationFilter && filteredQuestionsList.length === 0)) && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, px: 2, display: 'block' }}
              >
                No questions for this Best Practice match your current filters. Adjust or clear them to see more.
              </Typography>
             )}
          </Grid>
          {questionnaire.type === QuestionnaireTypes.MPA_BEST_PRACTICE && <Grid item xs={12} md={4}>
            <QuestionnaireSidebar
              readOnly={readOnly}
              handleSetShowAutoAnsweredByCertModal={handleSetShowAutoAnsweredByCertModal}
              incompleteQuestionIds={workflow?.status && isAssessmentWfStatus(workflow.status) ? incompleteAssessmentQuestionIds : incompleteQuestionIds}
              handleToggleIncompleted={handleToggleIncomplete}
              showIncompleteOnly={showIncompleteOnly}
            />
          </Grid>}
          </>
          )}
          <StandardDialog title={`${questionnaire && questionnaire.title } Complete`} handleClose={() => setShowBaselineCompleteModal(false)} isOpen={isQuestionniareBaseline && baselineCompleted && showBaselineCompleteModal && !questionnaireSubmitted}>
            <Typography>You have answered all questions on the {questionnaire.title}.
              {isQuestionniareBaseline && ` Your next step is to complete the TPN Best Practices Questionnaire for this ${survey?.application ? 'app' : 'site'}. You may also upload accepted non-TPN certifications as self-attestation for ${siteOrApplicationName ? ` ${siteOrApplicationName}` : 'this entitiy'}` }
              {!questionnaire.type && ' Are you sure you want to proceed with submission? If so, you will be taken back to your profile dashboard.'}
            </Typography>
            <StandardDialogActions>
            <>
              {(newSurveyId || showNavigateToQuestionnaireButton) && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={navigateAndReload}
                >
                  Submit and Continue
                </Button>
              )}
              {(!newSurveyId && !showNavigateToQuestionnaireButton) && (
                <Button variant="contained" color="primary" disabled>
                  <BouncingDotsLoader />
                  <span style={{ marginLeft: '8px' }}>Loading...</span>
                </Button>
              )}
            </>
              {[QuestionnaireTypes.SITE_BASELINE, QuestionnaireTypes.APPLICATION_BASELINE, QuestionnaireTypes.MPA_BEST_PRACTICE].indexOf(questionnaire.type!) === -1 && <Link to='/profile/'><Button variant="contained" color="primary">Return to Profile</Button></Link>}
            </StandardDialogActions>
          </StandardDialog>
          <StandardDialog title={`${questionnaire && questionnaire.title } Complete`} handleClose={() => handleQuestionnaireSubmittedConfirmClose(questionnaire.type === QuestionnaireTypes.MPA_BEST_PRACTICE )} isOpen={!isQuestionniareBaseline && isQuestionnaireComplete && questionnaireSubmittedConfirm && !questionnaireSubmitted}>
            <Typography>
            {isUserCompanyAdmin ? QUESTIONNAIRE_SUBMISSION_MESSAGE.admin : QUESTIONNAIRE_SUBMISSION_MESSAGE.employee }
            </Typography>
            <ApiErrorAlert
              show={showSubmitForAssessmentError}
              title="Questionnaire Submission Error"
              message="Your questionnaire could not be submitted. Please try again."
              onClose={() => setShowSubmitForAssessmentError(false)}
            />
            <StandardDialogActions>
              <Button
                variant="outlined"
                disabled={isSubmittingForAssessment}
                onClick={() => handleQuestionnaireSubmittedConfirmClose(questionnaire.type === QuestionnaireTypes.MPA_BEST_PRACTICE)}
                >Continue Editing
              </Button>
              {isUserCompanyAdmin && (
                <Button
                  variant="contained"
                  onClick={submitForAssessment}
                  disabled={isSubmittingForAssessment}
                  endIcon={!isSubmittingForAssessment && <NavigateNextIcon />}
                >
                  {isSubmittingForAssessment ? (
                    <>
                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                      Submitting...
                    </>
                  ) : (
                    'Submit'
                  )}
                </Button>
              )}
            </StandardDialogActions>
          </StandardDialog>

          <StandardDialog
            title={`${questionnaire ? questionnaire.title : 'TPN Best Practices Questionnaire'} Submitted`}
            handleClose={handleQuestionnaireSubmittedClose}
            isOpen={questionnaireSubmitted}
            hideBackdrop={true}>
            <Typography mb={1}>Congratulations! You have earned a TPN Blue Shield for {siteOrApplicationName && ` ${siteOrApplicationName}`}. You are now able to download the TPN Blue Shield logo by clicking on the icon in your TPN+ profile. You can also download your completed TPN Questionnaire PDF.</Typography>
            <Typography>PLEASE NOTE: you must update your TPN Questionnaire on an annual basis to continue use of the TPN Blue Shield logo for {siteOrApplicationName && ` ${siteOrApplicationName}`}.</Typography>
            <StandardDialogActions>
              <Button variant="outlined" onClick={() => handleQuestionnaireSubmittedClose()}>Close</Button>
              <Link to='/profile/'><Button variant="contained" color="primary">Back to Profile</Button></Link>
            </StandardDialogActions>
          </StandardDialog>

          <StandardDialog title="TPN Assessment Complete" handleClose={handleAssessmentCompleteClose} isOpen={!loadingAssessmentQACount && assessmentComplete}>
            <Typography>You have completed this assessment. Would you like to submit it for approval?</Typography>
            <StandardDialogActions>
              <Button
                variant="outlined"
                onClick={handleAssessmentCompleteClose}
                disabled={isSubmittingAssessment}
              >
                Close
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={submitForApproval}
                disabled={isSubmittingAssessment}
                endIcon={!isSubmittingAssessment && <NavigateNextIcon />}
              >
                {isSubmittingAssessment ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Submitting…
                  </>
                ) : (
                  'Submit'
                )}
              </Button>
            </StandardDialogActions>
          </StandardDialog>
          <StandardDialog title="TPN Assessment Submitted" handleClose={handleAssessmentSubmittedClose} isOpen={assessmentSubmitted}>
            <Typography>You have Submitted this assessment. TPN will review and be in touch soon.</Typography>
            <StandardDialogActions>
              <Button variant="outlined" onClick={() => handleAssessmentSubmittedClose()}>Close</Button>
              <Link to='/profile/'><Button variant="contained" color="primary">Profile</Button></Link>
            </StandardDialogActions>
          </StandardDialog>
          <StandardDialog title="Remediation Updates Provided" handleClose={handleRemediationCompletedClose} isOpen={remediationCompleted && user?.type === USER_TYPES.VENDOR_ADMIN}>
            <Typography>Congratulations! You have entered plans for all remediation items for the TPN Best Practices {wfQuestionnaireVersion} Assessment for {siteOrApplicationName && ` ${siteOrApplicationName}`} and you have earned your TPN { isCurrWfPreFiveThree ? 'Gold' : 'Silver' } Shield! You are now able to download the TPN { isCurrWfPreFiveThree ? 'Gold' : 'Silver' } Shield logo by clicking on the icon in your TPN+ profile.</Typography>
            <Typography>Your downloadable assessment report, which Content Owners can also access, now includes your remediation plans and will be updated as you complete them.</Typography>
            <StandardDialogActions>
                  <Box sx={{ flexGrow: 1 }}>
                      <Button variant="outlined" onClick={() => handleRemediationCompletedClose()}>Close</Button>
                  </Box>
                  <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<FileDownload />}
                      onClick={handleGenerateGoldShield}
                  >
                    TPN { isCurrWfPreFiveThree ? 'Gold' : 'Silver' } Status PDF
                  </Button>
                  <Link to='/profile/'>
                      <Button variant="contained" color="primary">Profile</Button>
                  </Link>
            </StandardDialogActions>
          </StandardDialog>
        </Grid>
  );
},
);

export default QuestionnaireAnswer;
